// Plant care-profile model constants, ported from the design handoff
// (reference/Cleverplant.dc.html + model_constants.json, with nutrition added).
// Key/insertion order matters: lvlAsc() reverses base_bands key order to get
// the low->high level ordering used by the base-level sliders.

export const C = {
	month_doy: [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335],
	base_bands: {
		moisture: { wet: [40, 55, 75, 90], moist: [25, 38, 58, 75], average: [18, 28, 45, 65], dry: [8, 15, 30, 45], arid: [3, 8, 18, 30] },
		temperature: { warm: [12, 18, 27, 32], temperate: [5, 13, 24, 30], cool: [2, 8, 18, 26] },
		// Light bands are daily light integrals (DLI) in lux·h/day — target total
		// light per day for each level (shade -> high), not instantaneous lux.
		// They compare directly to the device's daily_light_integral (lux·h).
		// Scaled for INDOOR light: the sensor caps around ~3,500 lux and office
		// plants never see outdoor levels, so these reflect realistic indoor light
		// (~50-3,500 lux over a ~10h indoor day), not horticultural/outdoor values.
		light: { high: [12000, 20000, 35000, 45000], medium: [5000, 10000, 22000, 30000], low: [2000, 4000, 10000, 15000], shade: [500, 1500, 5000, 8000] },
		nutrition: { tolerant: [500, 1200, 2400, 3600], moderate: [400, 900, 1800, 2800], sensitive: [300, 600, 1200, 1800] },
	},
	classes: {
		aseasonal: { baseline: 1.0, peaks: [], temp_amp: 1, label: 'aseasonal (tropical evergreen)' },
		evergreen: { baseline: 0.8, peaks: [['summer', 0.2, 2.5]], temp_amp: 3, label: 'evergreen, weakly seasonal' },
		'temperate-deciduous': { baseline: 0.45, peaks: [['summer', 0.55, 2.5]], temp_amp: 6, label: 'temperate deciduous' },
		'summer-grower': { baseline: 0.3, peaks: [['summer', 0.7, 2.5]], temp_amp: 8, label: 'summer-grower, dry winter rest' },
		'winter-grower': { baseline: 0.3, peaks: [['winter', 0.7, 2.5]], temp_amp: 5, label: 'winter-grower, summer rest' },
		'bimodal-stepped': { baseline: 0.04, peaks: [['spring', 0.86, 1.0], ['autumn', 0.95, 1.0]], temp_amp: 4, label: 'bimodal/stepped (Lithops-type)' },
	},
	north_season: { spring: 4, summer: 7, autumn: 10, winter: 1 },
	default_bands: { moisture: 'average', light: 'medium', temperature: 'warm', nutrition: 'moderate' },
	default_class: 'evergreen',
	plot_style: {
		moisture: { unit: 'soil moisture (% VWC)', tol: '#74c0fc', opt: '#1c7ed6', line: '#0b4f9c', bg: '#f3f8fe' },
		temperature: { unit: 'temperature (°C)', tol: '#ffa8a8', opt: '#f03e3e', line: '#a51111', bg: '#fff5f5' },
		light: { unit: 'daily light integral (lux·h)', tol: '#ffd43b', opt: '#f59f00', line: '#b45309', bg: '#fffaf0' },
		nutrition: { unit: 'EC (µS/cm)', tol: '#b197fc', opt: '#7048e8', line: '#5028c0', bg: '#f6f3fe' },
	},
	status_colors: { optimal: '#2f9e44', tolerated: '#f59f00', out: '#e03131' },
};

export const METRICS = ['moisture', 'temperature', 'light', 'nutrition'];

// Swatch colors for the base-level control headers (per handoff).
export const SWATCH = { moisture: '#1c7ed6', temperature: '#ff6b6b', light: '#fab005', nutrition: '#7048e8' };

export const METRIC_LABELS = { moisture: 'Moisture', temperature: 'Temperature', light: 'Light', nutrition: 'Nutrition' };

// Metrics whose health check grades against a plant profile band, and which can
// therefore be previewed on real devices. Nutrition is still graded against fixed
// thresholds, so there is nothing to preview for it yet.
// Must match PreviewableMetrics in internal/service/sensor_preview_service.go.
export const PREVIEWABLE_METRICS = ['temperature', 'light', 'moisture'];

// Categorical series colors for the multi-device preview chart, in fixed slot
// order — one per device line, up to the 8-device selection cap.
//
// Validated as a set for the light chart surface: every adjacent pair clears the
// colorblind-separation and normal-vision floors. Three of them (aqua, yellow,
// magenta) sit below 3:1 contrast against white, which is why the chart always
// ships a legend and a per-device summary strip: identity is never carried by
// color alone. Assign these in fixed order and never generate a 9th.
export const DEVICE_SERIES_COLORS = [
	'#2a78d6', // blue
	'#eb6834', // orange
	'#1baf7a', // aqua
	'#eda100', // yellow
	'#e87ba4', // magenta
	'#008300', // green
	'#4a3aa7', // violet
	'#e34948', // red
];

export const PHENOLOGY_OPTIONS = Object.keys(C.classes).map((key) => ({ value: key, label: C.classes[key].label }));

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Default starting parameters for a family that has never been configured.
// The user adjusts these in the editor; nothing is persisted until they save.
export function defaultParameters() {
	const baseLevels = {};
	for (const metric of METRICS) {
		const name = C.default_bands[metric];
		const asc = Object.keys(C.base_bands[metric]).slice().reverse();
		const idx = asc.indexOf(name);
		baseLevels[metric] = idx < 0 ? 0 : idx;
	}
	const cls = C.default_class;
	return {
		phenology_class: cls,
		hemisphere: 'north',
		base_levels: baseLevels,
		peaks: C.classes[cls].peaks.map((p) => [C.north_season[p[0]], p[1], p[2]]),
		band_scale: { opt: 1, tol: 1 },
	};
}
